import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ActivateReferenceDocument,
  type ActivateReferenceMutation,
  type ActivateReferenceMutationVariables,
  DeactivateReferenceDocument,
  type DeactivateReferenceMutation,
  type DeactivateReferenceMutationVariables,
} from "~/features/references/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Activate / deactivate a reference (`cnut(De)activateReference`). Returns plain
 * submitters + a `processing` flag; the calling container owns the confirm dialog.
 */
export function useReferenceActivation() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [activate] = useMutation<ActivateReferenceMutation, ActivateReferenceMutationVariables>(
    ActivateReferenceDocument,
  );
  const [deactivate] = useMutation<DeactivateReferenceMutation, DeactivateReferenceMutationVariables>(
    DeactivateReferenceDocument,
  );

  const run = useCallback(
    async (id: string, shouldDeactivate: boolean) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        if (shouldDeactivate) {
          await deactivate({ variables: { input: { tenantId, id } } });
        } else {
          await activate({ variables: { input: { tenantId, id } } });
        }
        toast.success(
          shouldDeactivate
            ? t("feature.common.deactivatedSuccessfully", { model: "reference" })
            : t("feature.common.activatedSuccessfully", { model: "reference" }),
        );
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [activate, deactivate, tenantId, t],
  );

  const onActivate = useCallback((id: string) => run(id, false), [run]);
  const onDeactivate = useCallback((id: string) => run(id, true), [run]);

  return { onActivate, onDeactivate, processing };
}
